import { useDispatch } from 'react-redux'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Broker_OrderType, Broker_Variety, Order, OrderStatus, OrderType, TransactionType, Validity } from '../../../proto/generated/order'
import { cancelOrder, placeOrModifyOrder } from '../../../services/apis/orders'
import { Box, Dialog, DialogContent, DialogTitle, Stack } from '@mui/material'
import { ExtraSmallButton } from '../../custom'
import { setLoading } from '../../../reducer/alerts.reducer'
import { PlaceOrModifyOrdersRequest, TradingAccountStatus } from '../../../proto/generated/trading-bridge'
import { openConnectAccountDialog, updateOrderHistory } from '../../../reducer/orders.reducer'
import AnalyticsManager from '../../../analytics/AnalyticsManager'
import { getSecurityAsync, getSecurityKey } from '../../../util/security'
import { setBasketId, setBatchId, setWaitingForOrders, updateTradeOrders, updateExitStrategy, updateTradeOrder } from '../../../reducer/strategyBuilder.reducer'
import {
    selectBatchId,
    selectExitStrategy,
    selectOrderType,
    selectStrategyBuilderError,
    selectStrategyBuilderMode,
    selectStrategyBuilderTradingAccountId,
    selectStrategyGroup,
    selectTradeOrders,
    selectTradeProduct,
    selectWaitingForOrders
} from '../../../selectors/strategyBuilder'
import { v4 as uuid } from 'uuid'
import { selectPrimaryTradingAccount, selectTradingAccounts, selectUserDetails } from '../../../selectors/user'
import { selectOrdersHistory } from '../../../selectors/orders'
import commonActions from '../../../actions/common.actions'
import { GroupedPosition } from '../../../reducer/positions.reducer'
import { ModifyType } from '../../ordersWidget/OrderRow'
import SlAndTargetInputs from './slAndTargetInputs'
import { calculatePriceToSend, reverseCalculateInputValue } from './slTargetUtils'

interface SlTargetDialogProps {
    open: boolean
    onClose: () => void
    position?: GroupedPosition
    modifyType?: ModifyType
    /** Fallback path used when no GroupedPosition is available (e.g. basket SL/TP modify from Orders table without a position row). */
    sourceOrder?: Order
}
const SlTargetDialog: React.FC<SlTargetDialogProps> = ({ open, onClose, position, modifyType, sourceOrder }) => {
    const dispatch = useDispatch()
    const mode = useSelector(selectStrategyBuilderMode)
    const user = useSelector(selectUserDetails)
    const placedOrders = useSelector(selectOrdersHistory)
    const tradingAccounts = useSelector(selectTradingAccounts)
    const { addStopLoss, addTarget, slValue, targetValue } = useSelector(selectExitStrategy)
    // Track validation state from SlAndTargetInputs component
    const [isSlTargetValid, setIsSlTargetValid] = useState(true)

    const orderHistory = useSelector(selectOrdersHistory)
    const strategyTradingAccountId = useSelector(selectStrategyBuilderTradingAccountId)
    const selectedTradingAccount = useSelector(selectPrimaryTradingAccount)
    const error = useSelector(selectStrategyBuilderError)
    const strategyGroup = useSelector(selectStrategyGroup)
    const tradeOrders = useSelector(selectTradeOrders)
    const batchId = useSelector(selectBatchId)
    const product = useSelector(selectTradeProduct)
    const orderType = useSelector(selectOrderType)
    const waitingForOrders = useSelector(selectWaitingForOrders)

    const orderIdToOrderMap = useMemo(() => {
        if (!placedOrders) return {} as Record<string, Order>

        return placedOrders.reduce(
            (acc, o) => {
                acc[o.orderId] = o
                return acc
            },
            {} as Record<string, Order>
        )
    }, [placedOrders])
    const tradingAccount = useMemo(() => {
        if (mode !== 'trade') {
            return tradingAccounts.find((t) => t.accountId === strategyTradingAccountId) ?? selectedTradingAccount
        }
        return selectedTradingAccount
    }, [mode, selectedTradingAccount, strategyTradingAccountId, tradingAccounts])
    const basketOpenSlOrder = useMemo(() => {
        return (orderHistory || []).find((o) => {
            return o.basketId === position?.groupId && [OrderStatus.OPEN, OrderStatus.PENDING, OrderStatus.TRIGGER_PENDING].includes(o.status) && o.orderType === OrderType.STOP_LOSS
        })
    }, [orderHistory, position?.groupId])
    const basketOpenTargetOrder = useMemo(() => {
        return (orderHistory || []).find((o) => {
            return o.basketId === position?.groupId && [OrderStatus.OPEN, OrderStatus.PENDING, OrderStatus.TRIGGER_PENDING].includes(o.status) && o.orderType === OrderType.TARGET
        })
    }, [orderHistory, position?.groupId])

    const basketInactiveSlOrder = useMemo(() => {
        return (orderHistory || []).find((o) => {
            return o.basketId === position?.groupId && o.status === OrderStatus.INACTIVE && o.orderType === OrderType.STOP_LOSS
        })
    }, [position?.groupId, orderHistory])

    const basketInactiveTargetOrder = useMemo(() => {
        return (orderHistory || []).find((o) => {
            return o.basketId === position?.groupId && o.status === OrderStatus.INACTIVE && o.orderType === OrderType.TARGET
        })
    }, [position?.groupId, orderHistory])

    /** Source-order fallback: opened from Orders table for a basket SL/TP without a matching `GroupedPosition`. */
    const useSourceOrderFallback = !position && !!sourceOrder
    const isSourceSlRow = useSourceOrderFallback && sourceOrder?.orderType === OrderType.STOP_LOSS
    const isSourceTpRow = useSourceOrderFallback && sourceOrder?.orderType === OrderType.TARGET

    /** Resolve security for the source-order leg to get `priceMultiplier` for prefill / submit scaling. */
    const [sourceOrderSecurity, setSourceOrderSecurity] = useState<Awaited<ReturnType<typeof getSecurityAsync>> | undefined>(undefined)
    useEffect(() => {
        if (!useSourceOrderFallback || !sourceOrder?.security?.code || sourceOrder.security.exchange == null) {
            setSourceOrderSecurity(undefined)
            return
        }
        let cancelled = false
        getSecurityAsync(getSecurityKey(sourceOrder.security.exchange, sourceOrder.security.code)).then((s) => {
            if (!cancelled) setSourceOrderSecurity(s)
        })
        return () => {
            cancelled = true
        }
    }, [useSourceOrderFallback, sourceOrder?.security?.code, sourceOrder?.security?.exchange])

    const hasSL = useSourceOrderFallback ? isSourceSlRow : !!basketOpenSlOrder
    const hasTarget = useSourceOrderFallback ? isSourceTpRow : !!basketOpenTargetOrder

    // Determine if this is a single security or basket position
    const isSingleSecurity = useMemo(() => {
        return (position?.children?.length ?? 0) === 1 && !!position?.groupId?.startsWith('default_')
    }, [position?.children, position?.groupId])

    // Clear SL/Target values when dialog opens or position changes (skip in source-order fallback so the inputs init effect can prefill).
    useEffect(() => {
        if (open && !useSourceOrderFallback) {
            dispatch(
                updateExitStrategy({
                    addStopLoss: false,
                    addTarget: false,
                    slValue: '',
                    targetValue: ''
                })
            )
        }
    }, [open, position?.groupId, useSourceOrderFallback, dispatch])

    const onPlaceOrderClick = useCallback(async () => {
        if (tradingAccounts?.length === 0 || tradingAccount?.status !== TradingAccountStatus.ACTIVE) {
            AnalyticsManager.logEvent('Place Order Clicked')
            if (tradingAccounts?.length === 0) {
                dispatch(
                    openConnectAccountDialog({
                        type: 'connect',
                        tradingAccountId: ''
                    })
                )
            } else {
                dispatch(
                    openConnectAccountDialog({
                        type: 'reconnect',
                        tradingAccountId: tradingAccount.accountId
                    })
                )
            }
            return
        } else if (error) {
            return
        }

        if (useSourceOrderFallback && sourceOrder) {
            const isSL = sourceOrder.orderType === OrderType.STOP_LOSS
            const isTP = sourceOrder.orderType === OrderType.TARGET
            const multiplier = sourceOrderSecurity?.priceMultiplier ?? 100
            const slValueNum = typeof slValue === 'number' ? slValue : Number(slValue)
            const targetValueNum = typeof targetValue === 'number' ? targetValue : Number(targetValue)
            const updatedBatchId = uuid()

            /** Build a clean modify Order from `sourceOrder` identity + new SL/TP value, mirroring the position-flow shape (no server-only fields like `status`, `created`, `metadata`, etc.). */
            const modifyOrder: Order = {
                security: {
                    code: sourceOrder.security?.code ?? '',
                    exchange: sourceOrder.security?.exchange ?? 0
                },
                transactionType: sourceOrder.transactionType,
                orderType: sourceOrder.orderType,
                brokerOrderType: sourceOrder.brokerOrderType,
                quantity: sourceOrder.quantity,
                isBasket: true,
                basketId: sourceOrder.basketId,
                batchId: updatedBatchId,
                orderDate: Date.now(),
                product: sourceOrder.product,
                price: isTP && Number.isFinite(targetValueNum) && targetValueNum
                    ? Math.round(targetValueNum * multiplier)
                    : 0,
                triggerPrice: isSL && Number.isFinite(slValueNum) && slValueNum
                    ? Math.round(slValueNum * multiplier)
                    : 0,
                validity: sourceOrder.validity ?? Validity.DAY,
                brokerVariety: sourceOrder.brokerVariety ?? Broker_Variety.REGULAR,
                orderExecutionParams: {},
                clientOrderId: sourceOrder.clientOrderId ?? uuid(),
                orderId: sourceOrder.orderId,
                immediateExecutePaperOrder: false,
                ...(isSL ? { marketProtection: -1 } : {}),
                strategyGroup: sourceOrder.strategyGroup
            }

            const requestPayload: PlaceOrModifyOrdersRequest = {
                tradingAccountId: tradingAccount?.accountId,
                buyBeforeSell: true,
                order: [modifyOrder],
            }

            try {
                dispatch(setLoading(true))
                const result = await placeOrModifyOrder(requestPayload)
                const orderUpdates: Order[] = []
                result.orderResponse.forEach((response) => {
                    if (response.order) orderUpdates.push(response.order)
                })
                if (orderUpdates.length) {
                    dispatch(updateOrderHistory({ updates: orderUpdates }))
                }
            } catch (err) {
                console.log(err, 'error occurred')
                commonActions.setApiError(dispatch, {
                    error: true,
                    message: 'Error placing orders',
                    errorType: 'error'
                })
            } finally {
                dispatch(setLoading(false))
            }
            return
        }

        const updatedBasketId = position?.groupId || uuid()
        const updatedBatchId = uuid()
        AnalyticsManager.logEvent('Place Order Clicked')
        const orderDate = Date.now()
        let updateRejectedOrders = false
        const isEditingSL = !!basketOpenSlOrder?.orderId
        const isEditingTarget = !!basketOpenTargetOrder?.orderId

        const newTradeOrders = tradeOrders.map((to) => {
            const referenceOrder = to.referenceOrderId ? orderIdToOrderMap[to.referenceOrderId] : undefined
            if (referenceOrder && [OrderStatus.REJECTED, OrderStatus.FAILED].includes(referenceOrder.status)) {
                updateRejectedOrders = true
                return {
                    ...to,
                    referenceOrderId: undefined,
                    clientOrderId: uuid()
                }
            }
            return to
        })
        const request: PlaceOrModifyOrdersRequest = {
            tradingAccountId: tradingAccount?.accountId,
            buyBeforeSell: true,
            order: await Promise.all(
                newTradeOrders
                    .filter((t) => {
                        return t.selected && (!t.referenceOrderId || orderIdToOrderMap[t.referenceOrderId]?.status !== OrderStatus.EXECUTED)
                    })
                    .map(async (tradeOrder) => {
                        if (!tradeOrder.security) {
                            console.error('Security is not defined for order', tradeOrder)
                            throw new Error('Security is not defined for order')
                        }
                        const security = await getSecurityAsync(getSecurityKey(tradeOrder.security.exchange, tradeOrder.security.code))
                        if (!security) {
                            console.error('Security not present for trading', tradeOrder)
                            throw new Error('Security is not defined for order')
                        }
                        // const resolvedProduct = product === Product.INTRADAY ? Product.INTRADAY : Product.MARGIN
                        const referenceOrder = tradeOrder.referenceOrderId ? orderIdToOrderMap[tradeOrder.referenceOrderId] : undefined
                        const orderDetails: Order = {
                            security: {
                                code: security.code,
                                exchange: security.exchange
                            },
                            transactionType: tradeOrder.transactionType,
                            orderType: OrderType.NORMAL,
                            brokerOrderType: orderType,
                            quantity: tradeOrder.quantity,
                            product: position.originalPosition?.product || position.children[0]?.originalPosition.product,
                            price: orderType === Broker_OrderType.MARKET_ORDER ? 0 : tradeOrder.price,
                            triggerPrice: 0,
                            brokerVariety: Broker_Variety.REGULAR,
                            validity: Validity.DAY,
                            clientOrderId: uuid(),
                            orderDate: referenceOrder ? referenceOrder.orderDate : orderDate,
                            isBasket: !position.groupId.startsWith('default_'),
                            orderId: referenceOrder ? referenceOrder.orderId : undefined,
                            basketId: updatedBasketId,
                            batchId: updatedBatchId,
                            strategyGroup: position.groupName || strategyGroup,
                            immediateExecutePaperOrder: false
                        }
                        return orderDetails
                    })
            )
        }

        const isBasket = !position.groupId.startsWith('default_')
        const positionSec = await getSecurityAsync(getSecurityKey(position.security.exchange, position.security.code))
        const securityCode = isSingleSecurity ? position.security.code : (positionSec?.underlyingSecurityCode ?? '')
        const exchange = position.security.exchange

        if (addStopLoss && slValue) {
            // Calculate the actual price to send based on whether it's single security or basket
            let priceToSend = slValue

            if (isSingleSecurity) {
                // For single securities editing existing orders, use exact mode
                // For new orders, use points mode
                const valueType = hasSL ? 'exact' : 'points'
                priceToSend = calculatePriceToSend({
                    inputValue: slValue,
                    valueType,
                    position,
                    selectedSecurity: position.security,
                    isStopLoss: true
                })
            }
            // For baskets, slValue is already the MTM value, use as-is

            request.order.push({
                security: {
                    code: securityCode ?? '',
                    exchange: exchange ?? 0
                },
                transactionType: !isBasket ? (position.children[0]?.originalPosition?.closePosition > 0 ? TransactionType.SELL : TransactionType.BUY) : TransactionType.BUY,
                orderType: OrderType.STOP_LOSS,
                brokerOrderType: Broker_OrderType.STOPLOSS_MARKET_ORDER,
                quantity: 1 * position.quantity,
                isBasket: isBasket,
                basketId: updatedBasketId,
                batchId: updatedBatchId,
                orderDate: Date.now(),
                product: position.originalPosition?.product || position.children[0]?.originalPosition.product,
                price: 0,
                triggerPrice: Math.round(priceToSend * (position.security?.priceMultiplier ?? 100)),
                validity: Validity.DAY,
                brokerVariety: Broker_Variety.REGULAR,

                orderExecutionParams: {},
                clientOrderId: isEditingSL ? basketOpenSlOrder!.clientOrderId : uuid(),

                orderId: isEditingSL ? basketOpenSlOrder!.orderId : undefined,
                immediateExecutePaperOrder: false,
                marketProtection: -1,
                strategyGroup: position.groupName || strategyGroup
            })
        }

        if (addTarget && targetValue) {
            // Calculate the actual price to send based on whether it's single security or basket
            let priceToSend = targetValue

            if (isSingleSecurity) {
                // For single securities editing existing orders, use exact mode
                // For new orders, use points mode
                const valueType = hasTarget ? 'exact' : 'points'
                priceToSend = calculatePriceToSend({
                    inputValue: targetValue,
                    valueType,
                    position,
                    selectedSecurity: position.security,
                    isStopLoss: false
                })
            }
            // For baskets, targetValue is already the MTM value, use as-is

            request.order.push({
                security: {
                    code: securityCode ?? '',
                    exchange: exchange ?? 0
                },
                transactionType: !isBasket ? (position.children[0]?.originalPosition?.closePosition > 0 ? TransactionType.SELL : TransactionType.BUY) : TransactionType.BUY,
                orderType: OrderType.TARGET,
                brokerOrderType: Broker_OrderType.LIMIT_ORDER,
                quantity: 1 * position.quantity,
                isBasket: isBasket,
                basketId: updatedBasketId,
                batchId: updatedBatchId,
                orderDate: Date.now(),
                product: position.originalPosition?.product || position.children[0]?.originalPosition.product,
                price: Math.round(priceToSend * (position.security?.priceMultiplier ?? 100)),
                triggerPrice: 0,
                validity: Validity.DAY,
                brokerVariety: Broker_Variety.REGULAR,
                orderExecutionParams: {},
                clientOrderId: isEditingTarget ? basketOpenTargetOrder!.clientOrderId : uuid(),

                orderId: isEditingTarget ? basketOpenTargetOrder!.orderId : undefined,
                immediateExecutePaperOrder: false,
                strategyGroup: position.groupName || strategyGroup
            })
        }
        try {
            dispatch(setLoading(true))
            if (updateRejectedOrders) {
                dispatch(updateTradeOrders(newTradeOrders))
            }
            const result = await placeOrModifyOrder(request)
            const orderUpdates: Order[] = []
            result.orderResponse.forEach((response) => {
                const { order } = response
                if (!order) return
                if (order.orderType === OrderType.STOP_LOSS) {
                    order.triggerPrice = slValue * (position?.security?.priceMultiplier ?? 100)
                }
                if (order.orderType === OrderType.TARGET) {
                    order.price = targetValue * (position?.security?.priceMultiplier ?? 100)
                }
                dispatch(
                    updateTradeOrder({
                        clientOrderId: order.clientOrderId,
                        update: {
                            referenceOrderId: order.orderId
                        }
                    })
                )
                orderUpdates.push(order)
            })
            if (orderUpdates.length) {
                dispatch(updateOrderHistory({ updates: orderUpdates }))
                !waitingForOrders && dispatch(setWaitingForOrders(true) as any)
                dispatch(setBasketId(updatedBasketId))
                dispatch(setBatchId(updatedBatchId) as any)
            }
        } catch (error) {
            console.log(error, 'error occurred')
            commonActions.setApiError(dispatch, {
                error: true,
                message: 'Error placing orders',
                errorType: 'error'
            })
        } finally {
            dispatch(setLoading(false))
        }
    }, [
        mode,
        batchId,
        tradeOrders,
        orderIdToOrderMap,
        tradingAccount,
        error,
        waitingForOrders,
        product,
        orderType,
        strategyGroup,
        slValue,
        basketInactiveSlOrder,
        targetValue,
        basketInactiveTargetOrder,
        position,
        useSourceOrderFallback,
        sourceOrder,
        sourceOrderSecurity,
        dispatch,
        tradingAccounts
    ])

    const handleConfirmSlTarget = async () => {
        const cancelOrderIds: string[] = []
        const fallbackSourceOrderId = useSourceOrderFallback ? sourceOrder?.orderId : undefined
        const fallbackIsSourceSl = useSourceOrderFallback && sourceOrder?.orderType === OrderType.STOP_LOSS
        const fallbackIsSourceTarget = useSourceOrderFallback && sourceOrder?.orderType === OrderType.TARGET

        // Cancel existing SL order if checkbox is deselected
        if (hasSL && !addStopLoss) {
            const slOrderId = basketOpenSlOrder?.orderId ?? (fallbackIsSourceSl ? fallbackSourceOrderId : undefined)
            if (slOrderId) cancelOrderIds.push(slOrderId)
        }

        // Cancel existing Target order if checkbox is deselected
        if (hasTarget && !addTarget) {
            const targetOrderId = basketOpenTargetOrder?.orderId ?? (fallbackIsSourceTarget ? fallbackSourceOrderId : undefined)
            if (targetOrderId) cancelOrderIds.push(targetOrderId)
        }

        try {
            // Cancel orders first if needed
            if (cancelOrderIds.length) {
                const response = await cancelOrder({
                    dispatch,
                    body: {
                        tradingAccountId: tradingAccount?.accountId ?? '',
                        orderId: cancelOrderIds
                    }
                })

                // Update order history with cancelled orders
                const orderUpdates: Order[] = []
                response.orderResponse.forEach((orderResponse) => {
                    if (orderResponse.order) {
                        orderUpdates.push(orderResponse.order)
                    }
                })

                if (orderUpdates.length) {
                    dispatch(updateOrderHistory({ updates: orderUpdates }))
                }
            }

            // Only place orders if checkboxes are selected AND values are provided
            const shouldPlaceSL = addStopLoss && slValue
            const shouldPlaceTarget = addTarget && targetValue

            if (shouldPlaceSL || shouldPlaceTarget) {
                await onPlaceOrderClick()
            }
        } catch (error) {
            console.log(error, 'error occurred while confirming sl/target')
            commonActions.setApiError(dispatch, {
                error: true,
                message: 'Error placing orders',
                errorType: 'error'
            })
        } finally {
            onClose()
            dispatch(
                updateExitStrategy({
                    addStopLoss: false,
                    addTarget: false,
                    slValue: 0,
                    targetValue: 0
                })
            )
        }
    }
    const dialogTitle = useMemo(() => {
        if (modifyType === ModifyType.STOPLOSS) return 'Modify Stop Loss'
        if (modifyType === ModifyType.TARGET) return 'Modify Target'
        return hasSL || hasTarget ? 'Edit Stop Loss / Target' : 'Add Stop Loss / Target'
    }, [modifyType, hasSL, hasTarget])

    return (
        <Dialog
            open={open}
            fullWidth
            onClose={() => {
                onClose()
                dispatch(
                    updateExitStrategy({
                        addStopLoss: false,
                        addTarget: false,
                        slValue: 0,
                        targetValue: 0
                    })
                )
            }}
            PaperProps={{
                sx: {
                    maxWidth: modifyType ? 600 * 0.8 : 600
                }
            }}
        >
            <DialogTitle>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <span>{dialogTitle}</span>
                </Stack>
            </DialogTitle>
            <DialogContent>
                {/* In fallback, defer mounting inputs until source security loads so the prefill multiplier is correct on the first init effect. */}
                {(!useSourceOrderFallback || sourceOrderSecurity) && (
                    <SlAndTargetInputs
                        position={position}
                        disabled={false}
                        disabledCheckbox={false}
                        disableStopLoss={hasSL}
                        disableTarget={hasTarget}
                        isEditingExistingSL={hasSL && isSingleSecurity}
                        isEditingExistingTarget={hasTarget && isSingleSecurity}
                        initialStopLoss={
                            useSourceOrderFallback && isSourceSlRow
                                ? (sourceOrder?.triggerPrice ?? 0) /
                                  (sourceOrderSecurity?.priceMultiplier ?? 100)
                                : hasSL
                                    ? reverseCalculateInputValue({
                                          storedPrice: (basketOpenSlOrder?.triggerPrice ?? 0) / (position?.security?.priceMultiplier ?? 100),
                                          position,
                                          selectedSecurity: position?.security,
                                          isStopLoss: true,
                                          valueType: isSingleSecurity ? 'exact' : undefined
                                      })
                                    : undefined
                        }
                        initialTarget={
                            useSourceOrderFallback && isSourceTpRow
                                ? (sourceOrder?.price ?? 0) /
                                  (sourceOrderSecurity?.priceMultiplier ?? 100)
                                : hasTarget
                                    ? reverseCalculateInputValue({
                                          storedPrice: (basketOpenTargetOrder?.price ?? 0) / (position?.security?.priceMultiplier ?? 100),
                                          position,
                                          selectedSecurity: position?.security,
                                          isStopLoss: false,
                                          valueType: isSingleSecurity ? 'exact' : undefined
                                      })
                                    : undefined
                        }
                        strategyMode={mode}
                        onValidationChange={setIsSlTargetValid}
                        showSLTargetInfo={true}
                        modifyType={modifyType}
                    />
                )}
                <Box mt={2} display="flex" justifyContent="flex-end">
                    <ExtraSmallButton onClick={handleConfirmSlTarget} variant="contained" color="primary" sx={{ width: 200 }} disabled={!isSlTargetValid}>
                        Submit
                    </ExtraSmallButton>
                </Box>
            </DialogContent>
        </Dialog>
    )
}
export default SlTargetDialog
